import { FC, ReactNode, useState } from 'react';
import { faInfoCircle, faPencil, faRobot, faRotateLeft, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import Rating from 'components/ui/Rating/Rating';
import Tooltip from 'components/ui/Tooltip/Tooltip';
import { getLogoUrl } from 'utils/utils';
import {
  TripPreviewDateBadge,
  TripPreviewFlightCard,
  TripPreviewHotelCard,
  TripPreviewTransferCard,
  TripPreviewSection,
  TripBuilderHotelDataDraftPayload,
} from 'utils/shared/tripResultBuilderPreview';
import { TripSlotRequirement } from 'ts/shared/interfaces/CreateProposalParams';
import { useTripBuilderPreview } from 'contexts/TripBuilderPreviewContext';
import TripBuilderHotelImage from './TripBuilderHotelImage';
import ProposalHotelDataModal, { ProposalHotelDataTab } from '../Single/ProposalHotelDataModal';
import Dropdown from '../../Dropdown';
import { getBlobUrl } from 'utils/shared/tripBuilderImages';
import EditProposalModal from '../Single/EditProposalModal';
import { gIco, trIco } from 'constants/generic';

const getCompoundFlightBackground = (color: string) => `color-mix(in srgb, ${color} 20%, white)`;

const slotRequirementOptions: Array<{ id: TripSlotRequirement; label: string }> = [
  { id: 'optional', label: 'Optional' },
  { id: 'mandatory', label: 'Mandatory' },
  { id: 'mandatory-first', label: 'Mandatory First' },
];

const getRequirementLabel = (value: TripSlotRequirement) =>
  slotRequirementOptions.find((option) => option.id === value)?.label ?? 'Mandatory';

const TripPreviewStatus: FC<{
  label: string;
  value?: TripSlotRequirement;
  onChange?: (value: TripSlotRequirement) => void;
  dateBadge?: TripPreviewDateBadge | null;
}> = ({ label, value, onChange, dateBadge }) => {
  const [closeOnClickDate, setCloseOnClickDate] = useState<Date>();
  const displayLabel = value ? getRequirementLabel(value) : label;

  if (!onChange) {
    return (
      <div className="pr-tb-status flex-column">
        {!!dateBadge && (
          <div className="pr-tb-badge flex-v-center">
            <span className="pr-tb-badge-day text-bold">{dateBadge.day}</span>
            <span className="pr-tb-badge-meta flex-column text-xsm">
              <span className="pr-tb-badge-month text-bold">{dateBadge.month}</span>
              <span className="pr-tb-badge-weekday">{dateBadge.weekDay}</span>
            </span>
          </div>
        )}
        <div className={dateBadge ? 'pr-tb-status-action' : undefined}>
          <span className="btn btn-default btn-sm" style={{ minWidth: 130 }}>
            {displayLabel}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="pr-tb-status flex-column">
      {!!dateBadge && (
        <div className="pr-tb-badge flex-v-center">
          <span className="pr-tb-badge-day text-bold">{dateBadge.day}</span>
          <span className="pr-tb-badge-meta flex-column text-xsm">
            <span className="pr-tb-badge-month text-bold">{dateBadge.month}</span>
            <span className="pr-tb-badge-weekday">{dateBadge.weekDay}</span>
          </span>
        </div>
      )}

      <div className={dateBadge ? 'pr-tb-status-action' : undefined}>
        <Dropdown
          byClick
          openerEmpty
          top={30}
          left={0}
          right={-20}
          closeOnClickDate={closeOnClickDate}
          align="left"
          dropdownClassName="pr-tb-status-menu text-xxs"
          opener={
            <button type="button" className="btn btn-default btn-sm" style={{ minWidth: 130 }}>
              {displayLabel}
            </button>
          }
        >
          {slotRequirementOptions.map((option) => (
            <div
              className="c-dropdown-row"
              key={option.id}
              onClick={() => {
                onChange(option.id);
                setCloseOnClickDate(new Date());
              }}
            >
              {option.label}
            </div>
          ))}
        </Dropdown>
      </div>
    </div>
  );
};

export const TripPreviewHotelOptionRow: FC<{
  section: TripPreviewSection;
  requirementValue?: TripSlotRequirement;
  onRequirementChange?: (value: TripSlotRequirement) => void;
  imageUrl?: string;
  city?: string;
  onImageUrlChange?: (url: string | undefined) => void;
  children: ReactNode;
  dateBadge?: TripPreviewDateBadge | null;
}> = ({ section, requirementValue, onRequirementChange, imageUrl, city, onImageUrlChange, children, dateBadge }) => (
  <div className="pr-tb-option display-flex">
    <div className="pr-tb-status-col flex-column">
      {!!onImageUrlChange && (
        <TripBuilderHotelImage imageUrl={imageUrl} city={city} onImageUrlChange={onImageUrlChange} />
      )}
      <TripPreviewStatus
        label={section.requirementLabel}
        value={requirementValue}
        onChange={onRequirementChange}
        dateBadge={dateBadge}
      />
    </div>
    <div className="pr-tb-option-body flex-column">{children}</div>
  </div>
);

export const TripPreviewOptionRow: FC<{
  section: TripPreviewSection;
  requirementValue?: TripSlotRequirement;
  onRequirementChange?: (value: TripSlotRequirement) => void;
  children: ReactNode;
  dateBadge?: TripPreviewDateBadge | null;
}> = ({ section, requirementValue, onRequirementChange, children, dateBadge }) => (
  <div className="pr-tb-option display-flex">
    <TripPreviewStatus
      label={section.requirementLabel}
      value={requirementValue}
      onChange={onRequirementChange}
      dateBadge={dateBadge}
    />
    <div className="pr-tb-option-body flex-column">{children}</div>
  </div>
);

const TripPreviewCardWrap: FC<{
  isExcluded: boolean;
  onToggleExclude: () => void;
  children: ReactNode;
  isDraft?: boolean;
  showSourceIcons?: boolean;
  onGoogleClick?: () => void;
  onTripadvisorClick?: () => void;
  onHotelInfoClick?: () => void;
  onAiHotelInfoClick?: () => void;
  onEditClick?: () => void;
  hasAiHotelInfo?: boolean;
}> = ({
  isExcluded,
  onToggleExclude,
  children,
  isDraft,
  showSourceIcons,
  onGoogleClick,
  onTripadvisorClick,
  onHotelInfoClick,
  onAiHotelInfoClick,
  onEditClick,
  hasAiHotelInfo,
}) => {
    const { t } = useTranslation();
    const onClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onToggleExclude();
    };

    const onSourceClick =
      (handler?: () => void) => (event: React.MouseEvent) => {
        event.stopPropagation();
        handler?.();
      };

    return (
      <div
        className={
          'pr-tb-card-wrap' +
          (isExcluded ? ' is-excluded' : '') +
          (isDraft ? ' is-draft' : '')
        }
      >
        {children}

        {showSourceIcons && !isExcluded && (
          <div className="pr-tb-card-actions">

            <span className="position-relative">
              <Tooltip content={t('hotel.info')}>
                <div
                  className="display-flex cur-poi pr-source-btn"
                  onClick={onSourceClick(onTripadvisorClick)}
                >
                  <FontAwesomeIcon
                    icon={faInfoCircle}
                    size="sm"
                    style={{ width: 16, height: 16 }}
                    className="cur-poi"
                    onClick={onSourceClick(onHotelInfoClick)}
                  />
                </div>
              </Tooltip>
            </span>
            {hasAiHotelInfo && (
              <span className="position-relative">
                <Tooltip content={t('proposal.ai_hotel_info')}>
                  <div
                    className="display-flex cur-poi pr-source-btn"
                    onClick={onSourceClick(onTripadvisorClick)}
                  >
                    <FontAwesomeIcon
                      icon={faRobot}
                      style={{ width: 16, height: 16 }}
                      size="sm"
                      className="cur-poi"
                      onClick={onSourceClick(onAiHotelInfoClick)}
                    />
                  </div>
                </Tooltip>
              </span>
            )}
            <span className="position-relative">
              <Tooltip content={t('hotel.google')}>
                <div
                  className="display-flex cur-poi pr-source-btn"
                  onClick={onSourceClick(onGoogleClick)}
                >
                  <img src={gIco} alt="g" className="pr-source-btn-img" />
                </div>
              </Tooltip>
            </span>
            <span className="position-relative">
              <Tooltip content={t('hotel.tripadvisor')}>
                <div
                  className="display-flex cur-poi pr-source-btn"
                  onClick={onSourceClick(onTripadvisorClick)}
                >
                  <img src={trIco} alt="t" className="pr-source-btn-img" />
                </div>
              </Tooltip>
            </span>
            {!!onEditClick && (
              <span className="position-relative">
                <Tooltip content={t('proposal.edit')}>
                  <div
                    className="display-flex cur-poi pr-source-btn"
                    onClick={onSourceClick(onEditClick)}
                  >
                    <FontAwesomeIcon
                      icon={faPencil}
                      size="sm"
                      style={{ width: 16, height: 16 }}
                      className="cur-poi"
                    />
                  </div>
                </Tooltip>
              </span>
            )}
          </div>
        )}
        <button
          type="button"
          className={'pr-tb-image-remove text-xxs flex-center' + (isExcluded ? ' pr-tb-card-restore' : '')}
          tabIndex={-1}
          onClick={onClick}
        >
          <FontAwesomeIcon icon={isExcluded ? faRotateLeft : faTimes} />
        </button>
      </div>
    );
  };

const TripPreviewFlightCardView: FC<{
  card: TripPreviewFlightCard;
  isExcluded: boolean;
  onToggleExclude: (key: string) => void;
  onSelect: () => void;
  isDraft?: boolean;
}> = ({ card, isExcluded, onToggleExclude, onSelect, isDraft }) => {
  const blobUrl = getBlobUrl();

  return (
    <TripPreviewCardWrap
      isExcluded={isExcluded}
      isDraft={isDraft}
      onToggleExclude={() => onToggleExclude(card.key)}
    >
      <button
        type="button"
        className={
          'pr-tb-card flex-column' +
          (card.isActive ? ' is-active' : '') +
          (card.compoundColor ? ' is-compound' : '')
        }
        style={
          card.compoundColor ? { backgroundColor: getCompoundFlightBackground(card.compoundColor) } : undefined
        }
        onClick={onSelect}
      >
        <div className="pr-tb-flight-head">
          {!!card.logoCode && (
            <div className="pr-tb-flight-logo">
                                    <img
                                        className="pr-tb-flight-logo-img" src={getLogoUrl(blobUrl, card.logoCode)} alt={card.logoCode} />
            </div>
          )}
          <div className="pr-tb-card-title text-sm text-bold">{card.route}</div>
        </div>
        <div className={'pr-tb-card-price flex-between text-xsm text-bold' + (card.priceLabel || card.scheduleLabel ? '' : ' is-empty')}>
          {!!card.scheduleLabel && <span className="pr-tb-flight-time text-xxs text-bold">{card.scheduleLabel}</span>}
          {!!card.priceLabel && <span className="pr-tb-card-amount text-black">{card.priceLabel}</span>}
        </div>
      </button>
    </TripPreviewCardWrap>
  )
};

const TripPreviewHotelCardView: FC<{
  card: TripPreviewHotelCard;
  isExcluded: boolean;
  onToggleExclude: (key: string) => void;
  onSelect: () => void;
  isDraft?: boolean;
  canEdit?: boolean;
  currentHotel?: any;
  onSaveHotelDataDraft?: (payload: TripBuilderHotelDataDraftPayload) => void;
  externalParam_1?: string;
}> = ({
  card,
  isExcluded,
  onToggleExclude,
  onSelect,
  isDraft,
  currentHotel,
  onSaveHotelDataDraft,
  externalParam_1,
}) => {
    const [showHotelDataPopup, setShowHotelDataPopup] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [hotelDataTab, setHotelDataTab] = useState<ProposalHotelDataTab>('google');
    const { getProductForCard, onSaveProductEdit } = useTripBuilderPreview();
    const proposalProduct = getProductForCard(card.key);
    const showSourceIcons = !!currentHotel;
    const hasAiHotelInfo = !!currentHotel?.aI_HotelInformatiom?.content?.length;

    return (
      <>
        <TripPreviewCardWrap
          isExcluded={isExcluded}
          onToggleExclude={() => onToggleExclude(card.key)}
          isDraft={isDraft}
          showSourceIcons={showSourceIcons}
          hasAiHotelInfo={hasAiHotelInfo}
          onGoogleClick={() => {
            setHotelDataTab('google');
            setShowHotelDataPopup(true);
          }}
          onTripadvisorClick={() => {
            setHotelDataTab('tripadvisor');
            setShowHotelDataPopup(true);
          }}
          onAiHotelInfoClick={() => {
            setHotelDataTab('ai_hotel_info');
            setShowHotelDataPopup(true);
          }}
          onHotelInfoClick={() => {
            setHotelDataTab('hotel_info');
            setShowHotelDataPopup(true);
          }}
          onEditClick={!isDraft && proposalProduct ? () => setShowEditModal(true) : undefined}
        >
          <button
            type="button"
            className={'pr-tb-card flex-column' + (card.isActive ? ' is-active' : '')}
            onClick={onSelect}
          >
            <Tooltip content={card.name} noPlague><div className="pr-tb-card-title text-sm text-bold">{card.name}</div></Tooltip>
            {!!card.star && <Rating value={card.star} className="pr-tb-card-rating" />}
            <div className={'pr-tb-card-price flex-between text-xsm text-bold text-black' + (card.priceLabel ? '' : ' is-empty')}>
              <span></span>
              {card.priceLabel || '\u00a0'}
            </div>
          </button>
        </TripPreviewCardWrap>
        {showEditModal && proposalProduct && (
          <EditProposalModal
            close={() => setShowEditModal(false)}
            proposalData={proposalProduct}
            onSave={(updatedProduct) => {
              onSaveProductEdit(updatedProduct);
              setShowEditModal(false);
            }}
          />
        )}
        {showHotelDataPopup && currentHotel && (
          <ProposalHotelDataModal
            show={showHotelDataPopup}
            onClose={() => setShowHotelDataPopup(false)}
            hotelDataTab={hotelDataTab}
            setHotelDataTab={setHotelDataTab}
            currentHotel={currentHotel}
            externalParam_1={externalParam_1}
            onSaveDraft={(payload) => {
              onSaveHotelDataDraft?.(payload);
              setShowHotelDataPopup(false);
            }}
          />
        )}
      </>
    );
  };

export const TripPreviewFlightsRow: FC<{
  flights: TripPreviewFlightCard[];
  draftFlights?: TripPreviewFlightCard[];
  title?: string;
  onSelectFlight: (flightKey: string, isRoundTrip: boolean) => void;
}> = ({ flights, draftFlights, title, onSelectFlight }) => {
  const { excludedCardKeys, toggleExcludedCard, removeIdbFlight } = useTripBuilderPreview();

  return (
    <>
      {!!title && <div className="pr-tb-flight-label">{title}</div>}
      <div className="pr-tb-row display-flex">
        {flights.map((card) => (
          <TripPreviewFlightCardView
            key={card.key}
            card={card}
            isExcluded={!!excludedCardKeys[card.key]}
            onToggleExclude={toggleExcludedCard}
            onSelect={() => onSelectFlight(card.key, card.isRoundTrip)}
          />
        ))}
        {(draftFlights || []).map((card) => (
          <TripPreviewFlightCardView
            key={card.key}
            card={card}
            isExcluded={false}
            isDraft
            onToggleExclude={removeIdbFlight}
            onSelect={() => undefined}
          />
        ))}
      </div>
    </>
  );
};

export const TripPreviewHotelsRow: FC<{
  hotels: TripPreviewHotelCard[];
  draftHotels?: TripPreviewHotelCard[];
  slotKey: string;
}> = ({ hotels, draftHotels, slotKey }) => {
  const {
    canSave,
    excludedCardKeys,
    toggleExcludedCard,
    selectHotel,
    removeIdbHotel,
    getHotelForCard,
    getDraftHotelForCard,
    onSaveHotelDataDraft,
    onSaveDraftHotelData,
    getDraftHotelExternalParam,
    externalParam_1,
  } = useTripBuilderPreview();

  return (
    <div className="pr-tb-row display-flex">
      {hotels.map((card) => (
        <TripPreviewHotelCardView
          key={card.key}
          card={card}
          isExcluded={!!excludedCardKeys[card.key]}
          onToggleExclude={toggleExcludedCard}
          onSelect={() => selectHotel(slotKey, card.key)}
          canEdit={canSave}
          currentHotel={getHotelForCard(card.key)}
          onSaveHotelDataDraft={(payload) => onSaveHotelDataDraft(card.key, payload)}
          externalParam_1={externalParam_1}
        />
      ))}
      {(draftHotels || []).map((card) => (
        <TripPreviewHotelCardView
          key={card.key}
          card={card}
          isExcluded={false}
          isDraft
          onToggleExclude={removeIdbHotel}
          onSelect={() => undefined}
          canEdit
          currentHotel={getDraftHotelForCard(card.key)}
          onSaveHotelDataDraft={(payload) => onSaveDraftHotelData(card.key, payload)}
          externalParam_1={getDraftHotelExternalParam(card.key) || externalParam_1}
        />
      ))}
    </div>
  );
};

const TripPreviewTransferCardView: FC<{
  card: TripPreviewTransferCard;
  isExcluded: boolean;
  onToggleExclude: (key: string) => void;
  onSelect: () => void;
  isDraft?: boolean;
}> = ({ card, isExcluded, onToggleExclude, onSelect, isDraft }) => (
  <TripPreviewCardWrap
    isExcluded={isExcluded}
    isDraft={isDraft}
    onToggleExclude={() => onToggleExclude(card.key)}
  >
    <button
      type="button"
      className={'pr-tb-card flex-column' + (card.isActive ? ' is-active' : '')}
      onClick={onSelect}
    >
      <div className="pr-tb-card-title text-sm text-bold">{card.name}</div>
      <div
        className={
          'pr-tb-card-price flex-between text-xsm text-bold' + (card.priceLabel || card.scheduleLabel ? '' : ' is-empty')
        }
      >
        {!!card.scheduleLabel && <span className="pr-tb-flight-time text-xxs text-bold">{card.scheduleLabel}</span>}
        {!!card.priceLabel && <span className="pr-tb-card-amount text-black">{card.priceLabel}</span>}
      </div>
    </button>
  </TripPreviewCardWrap>
);

export const TripPreviewTransfersRow: FC<{
  transfers: TripPreviewTransferCard[];
  draftTransfers?: TripPreviewTransferCard[];
  slotKey: string;
}> = ({ transfers, draftTransfers, slotKey }) => {
  const {
    excludedCardKeys,
    toggleExcludedCard,
    selectTransfer,
    removeIdbTransfer,
  } = useTripBuilderPreview();

  return (
    <div className="pr-tb-row display-flex">
      {transfers.map((card) => (
        <TripPreviewTransferCardView
          key={card.key}
          card={card}
          isExcluded={!!excludedCardKeys[card.key]}
          onToggleExclude={toggleExcludedCard}
          onSelect={() => selectTransfer(slotKey, card.key)}
        />
      ))}
      {(draftTransfers || []).map((card) => (
        <TripPreviewTransferCardView
          key={card.key}
          card={card}
          isExcluded={false}
          isDraft
          onToggleExclude={removeIdbTransfer}
          onSelect={() => undefined}
        />
      ))}
    </div>
  );
};
